'use client';

import React from 'react';
import { Button } from './button';
import { EmptyState } from './empty-state';
import { ErrorState } from './error-state';
import { LoadingSkeleton } from './loading-skeleton';

type StateMessage = { title?: string; description?: string; actions?: React.ReactNode };
export type StateKitProps =
  | { state: 'loading'; label?: string }
  | { state: 'checking'; label?: string }
  | ({ state: 'empty' | 'no-matches' | 'denied' | 'not-found' } & StateMessage)
  | {
      state: 'failed';
      title?: string;
      description?: string;
      onRetry: () => void;
      retryLabel?: string;
    }
  | { state: 'partial'; children: React.ReactNode; failedRegion: string; onRetry: () => void };

/**
 * Explicit states keep unavailable counts distinct from an empty result.
 * Empty results use one sentence and one caller-owned action (never an invented permission).
 * @example
 * <StateKit state="empty" title="No timesheets to approve." actions={<Button onClick={openRoster}>Open roster</Button>} />
 */
export function StateKit(props: StateKitProps) {
  switch (props.state) {
    case 'loading':
      return (
        <div aria-busy="true" className="min-w-0">
          <span className="sr-only">{props.label ?? 'Loading records'}</span>
          <LoadingSkeleton />
        </div>
      );
    case 'checking':
      return (
        <p role="status" className="min-w-0 break-words font-body text-body text-text-muted">
          {props.label ?? 'Checking…'}
        </p>
      );
    case 'failed':
      return (
        <ErrorState
          title={props.title ?? 'Couldn’t load this section'}
          message={props.description ?? 'Try again to load the records.'}
          onRetry={props.onRetry}
          retryLabel={props.retryLabel}
        />
      );
    case 'partial':
      return (
        <div className="min-w-0 space-y-4">
          {props.children}
          <div
            role="group"
            aria-label={props.failedRegion}
            className="flex min-w-0 flex-wrap items-center gap-2"
          >
            <p
              role="status"
              className="min-w-0 flex-1 break-words font-body text-body text-text-muted [overflow-wrap:anywhere]"
            >
              {props.failedRegion} couldn’t load
            </p>
            <Button type="button" variant="secondary" size="toolbar" onClick={props.onRetry}>
              Retry
            </Button>
          </div>
        </div>
      );
    default: {
      const defaults = {
        empty: ['No records yet', 'Records will appear here when they are added.'],
        'no-matches': ['No matches', 'Try changing the search or filters.'],
        denied: ['Access unavailable', 'Ask your administrator if you need access.'],
        'not-found': ['Record not found', 'Check the link or return to the list.'],
      } as const;
      const [title, description] = defaults[props.state];
      const isEmpty = props.state === 'empty' || props.state === 'no-matches';
      return (
        <EmptyState
          title={props.title ?? (isEmpty ? props.description : undefined) ?? title}
          description={isEmpty ? undefined : (props.description ?? description)}
          variant={isEmpty ? 'compact' : 'default'}
          actions={props.actions}
          className="min-w-0 break-words rounded-card-comfort [overflow-wrap:anywhere]"
        />
      );
    }
  }
}
